<template>
  <div>
    <slot
      :availability-button-toggled="availabilityButtonToggled"
      :date-picker-open="datePickerOpen"
      :filter-sort-by="filterSortBy"
      :filtered-partners="filteredPartners"
      :geocoder="geocoder"
      :handle-availability-button="handleAvailabilityButton"
      :handle-close-availability-menu="handleCloseAvailabilityMenu"
      :handle-change-modal="handleChangeModal"
      :handle-close-modal="handleCloseModal"
      :loading="loading"
      :open-city-search-modal="openCitySearchModal"
      :search-new-date="searchNewDate"
    />
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleNotch,
  faMapMarker,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import clone from 'lodash/clone';
import merge from 'lodash/merge';
import padStart from 'lodash/padStart';
import { DateTime } from 'luxon';
import { mapState } from 'vuex';

import useSmartTime from '@/composables/use-smart-time';
import SearchType from '@/enums/SearchType';
import ParseErrors from '@/mixins/ParseErrors';
import User from '@/models/User';

library.add({
  faCircleNotch,
  faMapMarker,
  faTimes,
});

export default {
  name: 'VetsterCityFilter',

  mixins: [ParseErrors],

  props: {
    city: {
      type: String,
      default: null,
    },
    country: {
      type: String,
      default: null,
    },
    locale: {
      type: String,
      default: null,
    },
    province: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { validDateFrom } = useSmartTime();

    return { validDateFrom };
  },

  data() {
    return {
      availabilityButtonToggled: false,
      date: null,
      datePickerOpen: false,
      filteredPartners: null,
      geocoder: null,
      loading: false,
      openCitySearchModal: false,
    };
  },

  computed: {
    ...mapState('search', ['inputs']),
  },

  created() {
    this.loading = true;
  },

  async mounted() {
    this.geocoder = new MapboxGeocoder({
      countries: this.countries,
      accessToken: import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN,
      language: this.locale,
      placeholder: this.$t('search.start-typing-your-city-name'),
      types: 'place',
      minLength: 3,
      autocomplete: true,
    });

    await this.$store.commit('search/setInputs', {
      city: this.city,
      province: this.province,
      country: this.country,
    });

    this.loading = false;
  },

  methods: {
    handleAvailabilityButton() {
      if (this.availabilityButtonToggled) {
        const wasFilteredByAvailability = !!this.date;

        this.availabilityButtonToggled = false;
        this.date = null;

        if (wasFilteredByAvailability) {
          this.search();
        }
      } else {
        this.datePickerOpen = !this.datePickerOpen;
      }
    },

    async filterSortBy(sort) {
      return this.search({ sort });
    },

    async search(payload = {}) {
      try {
        this.loading = true;

        const response = await this.$api.post('p/search', merge(this.getPayload(), {
          date_from: this.date || this.validDateFrom(),
        }, payload));

        this.filteredPartners = response.data.data.map((result) => new User(result));
      } catch (e) {
        this.parseError(e);
      } finally {
        this.loading = false;
      }
    },

    timeAsString(time) {
      const timeString = padStart(time || 0, 4, '0');

      return `${timeString.substring(0, 2)}:${timeString.substring(2, 4)}`;
    },

    handleCloseAvailabilityMenu() {
      this.datePickerOpen = false;
    },

    getPayload() {
      return merge(clone(this.inputs), {
        page: 1,
        time_from: this.timeAsString(this.inputs.time_from),
        time_to: this.timeAsString(this.inputs.time_to),
        timezone: DateTime.local().zoneName,
        type: SearchType.City,
      });
    },

    async handleCloseModal() {
      this.openCitySearchModal = false;
      await this.$store.commit('search/setInputs', {
        city: this.city,
        province: this.province,
        country: this.country,
      });
    },

    handleChangeModal() {
      this.$store.commit('search/clearAddressInputs');
    },

    searchNewDate(date) {
      this.date = date?.ymd;
      this.datePickerOpen = false;
      this.search();
      this.availabilityButtonToggled = true;
    },
  },
};
</script>
